// Dashboard.jsx
// Main authenticated view. Shows a personal greeting, the interactive
// bubble map of all 8 pillars, and a logout button.
// Fetches the user's memory on mount to hydrate completion status.

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/MemoryContext';
import { useMemory } from '../hooks/useMemory';
import BubbleMap from '../components/BubbleMap';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { memory, loading } = useMemory();
  const navigate = useNavigate();

  // Derive display name: prefer business name from memory, fall back to
  // the portion of the email before @, fall back to "founder".
  const displayName =
    user?.email?.split('@')[0] ?? 'founder';

  // Company name shown in the central bubble.
  const companyName = memory?.business_name || 'Your Company';

  // List of completed pillar IDs from memory (empty array if not loaded yet).
  const completedPillars = memory?.completed_pillars ?? [];

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div style={styles.page}>
      {/* ── Logout button ─────────────────────────────────────────────── */}
      <div style={styles.topBar}>
        <button style={styles.logoutBtn} onClick={handleLogout}>
          Log out
        </button>
      </div>

      {/* ── Greeting ──────────────────────────────────────────────────── */}
      <div style={styles.greetingWrap}>
        <p style={styles.greeting}>
          Hello{displayName ? `, ${displayName}` : ''} — what are we going to
          work on today?
        </p>
      </div>

      {/* ── Bubble map ────────────────────────────────────────────────── */}
      <div style={styles.mapWrap}>
        {loading ? (
          <p style={styles.loadingText}>Loading your dashboard…</p>
        ) : (
          <BubbleMap
            companyName={companyName}
            completedPillars={completedPillars}
          />
        )}
      </div>

      {/* ── Legend hint ───────────────────────────────────────────────── */}
      <div style={styles.legend}>
        <span style={styles.legendItem}>
          <span style={{ ...styles.dot, background: '#FF6B35' }} />
          Recommended next
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.dot, background: '#4CAF50' }} />
          Completed
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.dot, background: '#0F3460' }} />
          Not started
        </span>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#1A1A2E',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: '48px',
  },

  // ── Top bar ────────────────────────────────────────────────────────
  topBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px 28px 0',
    boxSizing: 'border-box',
  },
  logoutBtn: {
    padding: '8px 18px',
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.5)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
  },

  // ── Greeting ───────────────────────────────────────────────────────
  greetingWrap: {
    width: '100%',
    maxWidth: '760px',
    padding: '40px 24px 8px',
    boxSizing: 'border-box',
  },
  greeting: {
    margin: 0,
    fontSize: 'clamp(22px, 4vw, 32px)',
    fontWeight: '300',
    color: '#fff',
    lineHeight: 1.35,
    letterSpacing: '-0.3px',
  },

  // ── Bubble map ─────────────────────────────────────────────────────
  mapWrap: {
    width: '100%',
    maxWidth: '760px',
    padding: '0 16px',
    boxSizing: 'border-box',
    overflowX: 'auto',        // lets the map scroll on very small screens
  },
  loadingText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '15px',
    textAlign: 'center',
    padding: '80px 0',
    margin: 0,
  },

  // ── Legend ─────────────────────────────────────────────────────────
  legend: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '12px',
    padding: '0 24px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '12px',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '0.2px',
  },
  dot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
};
